import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useUserStore } from '../store/userStore';

export interface AppNotification {
  notificationId: string;
  type: string;
  title: string;
  body: string;
  projectId: string | null;
  projectName: string | null;
  actionUrl?: string | null;
  read: boolean;
  createdAt: string;
}

export function useNotifications() {
  const { sessionConfig } = useUserStore();
  const entityId = sessionConfig?.entityId;
  const userId = sessionConfig?.userId;

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId || !userId) {
      setLoading(false);
      return;
    }

    const notifRef = collection(
      db,
      'entities', entityId, 'users', userId, 'notifications'
    );
    const q = query(notifRef, orderBy('createdAt', 'desc'), limit(50));

    const unsub = onSnapshot(q, (snap) => {
      const items: AppNotification[] = snap.docs.map((d) => ({
        notificationId: d.id,
        ...(d.data() as Omit<AppNotification, 'notificationId'>),
      }));
      setNotifications(items);
      setLoading(false);
    });

    return unsub;
  }, [entityId, userId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  async function markRead(notificationId: string) {
    if (!entityId || !userId) return;
    const ref = doc(db, 'entities', entityId, 'users', userId, 'notifications', notificationId);
    await updateDoc(ref, { read: true });
  }

  async function markAllRead() {
    if (!entityId || !userId) return;
    const unread = notifications.filter((n) => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach((n) => {
      const ref = doc(db, 'entities', entityId, 'users', userId, 'notifications', n.notificationId);
      batch.update(ref, { read: true });
    });
    await batch.commit();
  }

  return { notifications, unreadCount, loading, markRead, markAllRead };
}
